import isEqual from 'fast-deep-equal';
import { useEffect, useState } from 'react';

import {
  File,
  DocumentEdge,
  Document,
  useUserAvatarsThumbnailByIdsLazyQuery,
} from '@graphql/generated';
import { isUrlExpired } from '@hooks/useGetDocumentsByClientIdsQuery';
import { AvatarThumbnailCachePrefix } from '@src/constants/constants';
import {
  buildAvatarThumbnailCacheEntries,
  getAvatarUrlTimestamp,
  setAvatarUrlsWithTimestamp,
  shouldRefetchAvatarUrl,
} from '@utils/avatarUrlCache';
import { AsyncStorage } from '@utils/storage';

export const useGetUserAvatarsByIdsQuery = () => {
  const [avatarIds, setAvatarIds] = useState<string[]>([]);
  // here use useUserAvatarsThumbnailByIdsQuery because thumbnail is enough for display,
  // except change profile in my account page.

  // TA-10914 (PR review): this used to be a regular (auto-executing)
  // useQuery with `fetchPolicy: 'network-only'`, alongside the effect below
  // that ALSO imperatively called `refetch()` for the same variables —
  // Apollo re-executes an auto-fetching query on every `variables` change
  // with `network-only`, so every avatarIds update fired two network
  // requests instead of one. Switching to the lazy variant removes the
  // automatic execution entirely; the effect's explicit call below is the
  // only trigger.
  const [urlRefetch, { data: refetchData, error: refetchError }] =
    useUserAvatarsThumbnailByIdsLazyQuery({
      // `avatarIds` only ever holds ids that `checkUserAvatarURLByIds` already
      // confirmed need refetching (their cached URL is missing or expired), so
      // a cache-first read here would only ever be wrong — this must always
      // hit the network for a genuinely fresh signature.
      fetchPolicy: 'network-only',
      // TA-10428: userAvatarsByIds throws a hard GraphQL error for the whole
      // query when even one requested id has no avatar record (e.g. a
      // deleted/removed user). errorPolicy 'all' surfaces that as `error`
      // below instead of rejecting the query, so a single stale avatar id
      // can't crash chat-detail.
      errorPolicy: 'all',
    });

  useEffect(() => {
    if (avatarIds.length > 0) {
      // .catch() guards against refetch() rejecting for reasons errorPolicy
      // 'all' doesn't cover (e.g. a network error) — this call's result is
      // never awaited/consumed by the caller, so an uncaught rejection here
      // would go unhandled.
      urlRefetch({ variables: { ids: avatarIds } }).catch((e) =>
        // eslint-disable-next-line no-console
        console.log('Error refetching user avatar thumbnails', e)
      );
    }
  }, [avatarIds, urlRefetch]);

  useEffect(() => {
    if (refetchData?.userAvatarsByIds) {
      const entries = buildAvatarThumbnailCacheEntries(
        refetchData.userAvatarsByIds,
        AvatarThumbnailCachePrefix
      );
      setAvatarUrlsWithTimestamp(entries, Date.now()).catch((e) =>
        // eslint-disable-next-line no-console
        console.log('Error caching user avatar thumbnails', e)
      );
    }
  }, [refetchData]);

  useEffect(() => {
    if (refetchError) {
      console.log(
        'Error fromm useUserAvatarsByIdsThumbnailQuery',
        refetchError.message
      );
    }
  }, [refetchError]);

  const updateUserAvatarIds = (newIds: string[]) => {
    if (isEqual(avatarIds, newIds)) {
      return;
    }
    setAvatarIds(newIds);
  };

  function isDocumentEdge(edge: DocumentEdge | Document): edge is DocumentEdge {
    return '__typename' in edge && edge.__typename === 'DocumentEdge';
  }

  const checkUserAvatarURL = async (edges: DocumentEdge[] | Document[]) => {
    if ((edges.length ?? 0) > 0) {
      let docs: Document[];
      if (isDocumentEdge(edges[0])) {
        docs = (edges as DocumentEdge[])
          .map((ed: DocumentEdge) => ed.node)
          .filter((n) => n !== undefined) as Document[];
      } else {
        docs = edges as Document[];
      }
      const avatarIdsNeedsToRefetchUrl = await checkUserAvatarURL2(docs);
      if ((avatarIdsNeedsToRefetchUrl?.needToRefetch?.length ?? 0) > 0) {
        updateUserAvatarIds(avatarIdsNeedsToRefetchUrl?.needToRefetch ?? []);
      }
    }
  };

  const checkUserAvatars = async (avatars: File[]) => {
    const avatarIds = new Set<string>();
    avatars.forEach((m) => {
      if (m?.id) {
        avatarIds.add(m.id);
      }
    });
    checkUserAvatarURLByIds([...avatarIds]);
  };
  const checkUserAvatarURLByIds = async (ids: string[]) => {
    if ((ids.length ?? 0) > 0) {
      const avatarIdsNeedsToRefetchUrl = new Set<string>();
      const nowMs = Date.now();
      for (const item of ids) {
        const key = AvatarThumbnailCachePrefix + item;
        const url = await AsyncStorage.getItem(key);
        const fetchedAtMs = await getAvatarUrlTimestamp(key);
        if (shouldRefetchAvatarUrl(url, fetchedAtMs, nowMs)) {
          avatarIdsNeedsToRefetchUrl.add(item);
        }
      }

      updateUserAvatarIds([...avatarIdsNeedsToRefetchUrl]);
    }
  };

  const checkUserAvatarIds = async (avatarIds: string[]) => {
    if ((avatarIds.length ?? 0) > 0) {
      updateUserAvatarIds(avatarIds);
    }
  };

  const checkUserAvatarURL2 = async (docs: Document[]) => {
    if ((docs.length ?? 0) > 0) {
      const avatarIdsNeedsToRefetchUrl = new Set<string>();
      const avatarIdsWithUrl: { clientId: string; url: string }[] = [];
      const now = new Date();
      for (const item of docs) {
        if (item?.__typename === 'Document' && item?.clientId) {
          const url = await AsyncStorage.getItem(item.clientId);
          if (!url) {
            avatarIdsNeedsToRefetchUrl.add(item.clientId);
          } else if (isUrlExpired(url, now)) {
            avatarIdsNeedsToRefetchUrl.add(item.clientId);
          } else {
            avatarIdsWithUrl.push({ clientId: item.clientId, url });
          }
        }
      }

      return {
        needToRefetch: [...avatarIdsNeedsToRefetchUrl],
        urls: avatarIdsWithUrl,
      };
    }
  };

  return {
    checkUserAvatarURLByIds,
    checkUserAvatars,
    checkUserAvatarURL,
    checkUserAvatarIds,
  };
};
